"use client"

import React, { useState, useEffect, useCallback } from 'react'
import { useConfirm } from '../../provider/confirm.provider'
import { FiFileText, FiRefreshCw, FiX, FiCheck, FiPlus, FiAlertCircle } from 'react-icons/fi'
import { FaClipboardList, FaProjectDiagram, FaLayerGroup } from 'react-icons/fa'
import axiosInstance from '../../libs/axios'
import { useAuth } from '../../provider/auth.provider'
import toast from 'react-hot-toast'
import { ProjectManagement } from '../projects'

// ─── Types ────────────────────────────────────────────────────────────────────

type RequirementType = 'project' | 'activity' | 'common'

interface Requirement {
  id: number
  title: string
  content: string
  requirement_type: RequirementType
  project_id: number | null
  activity_id: number | null
  is_active: boolean
  created_at: string
  updated_at: string
  project?: { id: number; project_name: string; project_short_name: string }
  activity?: { id: number; activity_name: string }
  creator?: { id: number; first_name: string; last_name: string }
  updater?: { id: number; first_name: string; last_name: string }
}

interface Project {
  id: number
  project_name: string
  project_short_name: string
  activity_id: number
  activity?: { id: number; activity_name: string }
}

interface Activity { id: number; activity_name: string }

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<RequirementType, string> = {
  project: 'Project Specific',
  activity: 'Activity Wise',
  common: 'Common (All)',
}

const TYPE_COLORS: Record<RequirementType, string> = {
  project: 'bg-blue-100 text-blue-800 border-blue-200',
  activity: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  common: 'bg-purple-100 text-purple-800 border-purple-200',
}

const TypeBadge = ({ type }: { type: RequirementType }) => (
  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${TYPE_COLORS[type]}`}>
    {TYPE_LABELS[type]}
  </span>
)

const formatDate = (d: string) => {
  const dt = new Date(d)
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

// ─── Requirement Edit Drawer ──────────────────────────────────────────────────

interface EditDrawerProps {
  isOpen: boolean
  onClose: () => void
  requirement: Requirement | null
  onSaved: () => void
}

const EditDrawer: React.FC<EditDrawerProps> = ({ isOpen, onClose, requirement, onSaved }) => {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (requirement) {
      setTitle(requirement.title)
      setContent(requirement.content)
    }
  }, [requirement, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !content.trim()) { toast.error('Title and content are required'); return }
    if (!requirement) return
    try {
      setSaving(true)
      await axiosInstance.put(`/requirements/${requirement.id}`, { title, content })
      onSaved()
      onClose()
    } catch {
      // toast handled by axios interceptor
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen || !requirement) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FiFileText className="text-blue-800" size={18} />
              Edit Requirement
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              <TypeBadge type={requirement.requirement_type} />
              {requirement.project && <span className="ml-2 text-blue-700">{requirement.project.project_name}</span>}
              {requirement.activity && <span className="ml-2 text-emerald-700">{requirement.activity.activity_name}</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg">
            <FiX size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Requirement Details *</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={10}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
            />
          </div>
          <div className="text-xs text-gray-400 space-y-1">
            {requirement.creator && <p>Created by: {requirement.creator.first_name} {requirement.creator.last_name}</p>}
            <p>Created: {formatDate(requirement.created_at)}</p>
            <p>Last updated: {formatDate(requirement.updated_at)}</p>
            {requirement.updater && <p>Updated by: {requirement.updater.first_name} {requirement.updater.last_name}</p>}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSubmit as any} disabled={saving} className="px-5 py-2 text-sm font-medium text-white bg-blue-800 rounded-lg hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2">
            {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiCheck size={14} />}
            Update Requirement
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Project Requirements Drawer ─────────────────────────────────────────────

interface ProjectRequirementsDrawerProps {
  isOpen: boolean
  onClose: () => void
  project: Project | null
}

const ProjectRequirementsDrawer: React.FC<ProjectRequirementsDrawerProps> = ({ isOpen, onClose, project }) => {
  const { hasPermission, user } = useAuth()
  const { confirm } = useConfirm()
  const canCreate = hasPermission('OperationsRequirementsProjectActivity', 'create_project_specific')
  const canEdit   = hasPermission('OperationsRequirementsProjectActivity', 'edit_project_specific') && user?.role.id !== 7
  const canDelete = hasPermission('OperationsRequirementsProjectActivity', 'delete_project_specific')

  const [requirements, setRequirements] = useState<Requirement[]>([])
  const [activities, setActivities]     = useState<Activity[]>([])
  const [loading, setLoading]           = useState(false)

  // Create form state
  const [isCreating, setIsCreating]     = useState(false)
  const [newTitle, setNewTitle]         = useState('')
  const [newContent, setNewContent]     = useState('')
  const [newType, setNewType]           = useState<RequirementType>('project')
  const [newActivityId, setNewActivityId] = useState('')
  const [submitting, setSubmitting]     = useState(false)

  // Edit state
  const [editingReq, setEditingReq]     = useState<Requirement | null>(null)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)

  const fetchRequirements = useCallback(async () => {
    if (!project) return
    try {
      setLoading(true)
      // Fetch project-specific + activity-wide + common requirements
      const params: any = { is_active: true }
      const res = await axiosInstance.get('/requirements', { params })
      const all: Requirement[] = res.data.data.requirements || []
      // Filter relevant ones
      const relevant = all.filter(r =>
        r.requirement_type === 'common' ||
        (r.requirement_type === 'project' && r.project_id === project.id) ||
        (r.requirement_type === 'activity' && r.activity_id === project.activity_id)
      )
      setRequirements(relevant)
    } catch {
      toast.error('Failed to load requirements')
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => {
    if (isOpen && project) {
      fetchRequirements()
      setIsCreating(false)
      setNewTitle(''); setNewContent(''); setNewType('project'); setNewActivityId('')
    }
  }, [isOpen, project, fetchRequirements])

  useEffect(() => {
    axiosInstance.get('/activities/list', { params: { limit: 200, status: 'ACTIVE' } })
      .then(r => setActivities(r.data.data?.activities || []))
      .catch(() => {})
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim() || !newContent.trim()) { toast.error('Title and content are required'); return }
    if (!project) return
    try {
      setSubmitting(true)
      const payload: any = { title: newTitle.trim(), content: newContent.trim(), requirement_type: newType }
      if (newType === 'project') payload.project_id = project.id
      if (newType === 'activity') payload.activity_id = newActivityId ? Number(newActivityId) : project.activity_id
      await axiosInstance.post('/requirements', payload)
      toast.success('Requirement created')
      setIsCreating(false)
      setNewTitle(''); setNewContent(''); setNewType('project'); setNewActivityId('')
      fetchRequirements()
    } catch {
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (req: Requirement) => {
    const ok = await confirm({
      title: "Delete requirement",
      message: `Delete "${req.title}"? This cannot be undone.`,
      confirmText: "Delete requirement",
      variant: "danger",
    })
    if (!ok) return
    try {
      await axiosInstance.delete(`/requirements/${req.id}`)
      fetchRequirements()
    } catch {}
  }

  if (!isOpen || !project) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FaClipboardList className="text-orange-600" size={18} />
              Client Requirements
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              <span className="font-medium text-blue-700">{project.project_name}</span>
              {project.activity && <span className="text-gray-400"> · {project.activity.activity_name}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && !isCreating && (
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-800 text-white rounded-lg text-xs font-medium hover:bg-blue-900"
              >
                <FiPlus size={13} /> Add
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg">
              <FiX size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Create form */}
          {isCreating && (
            <form onSubmit={handleCreate} className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-gray-900 text-sm">New Requirement</h4>
                <button type="button" onClick={() => setIsCreating(false)} className="p-1 hover:bg-blue-100 rounded">
                  <FiX size={14} className="text-gray-500" />
                </button>
              </div>

              {/* Scope */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Scope</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['project', 'activity', 'common'] as RequirementType[]).map(t => (
                    <button key={t} type="button" onClick={() => setNewType(t)}
                      className={`py-1.5 px-2 rounded-lg border-2 text-xs font-medium transition-all ${newType === t ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Activity selector when type=activity */}
              {newType === 'activity' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Activity</label>
                  <select value={newActivityId} onChange={e => setNewActivityId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="">Use project's activity ({project.activity?.activity_name})</option>
                    {activities.map(a => <option key={a.id} value={a.id}>{a.activity_name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
                  placeholder="Requirement title..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Details *</label>
                <textarea value={newContent} onChange={e => setNewContent(e.target.value)}
                  placeholder="Describe the requirement..."
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 resize-y" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setIsCreating(false)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-blue-800 rounded-lg hover:bg-blue-900 disabled:opacity-50 flex items-center gap-1.5">
                  {submitting ? <FiRefreshCw size={12} className="animate-spin" /> : <FiCheck size={12} />}
                  Save
                </button>
              </div>
            </form>
          )}

          {/* Requirements list */}
          {loading ? (
            <div className="text-center py-10">
              <FiRefreshCw className="animate-spin mx-auto text-blue-800 mb-2" size={24} />
              <p className="text-sm text-gray-500">Loading requirements...</p>
            </div>
          ) : requirements.length === 0 ? (
            <div className="text-center py-10">
              <FiAlertCircle className="mx-auto text-gray-300 mb-2" size={36} />
              <p className="text-sm font-semibold text-gray-700">No requirements found</p>
              <p className="text-xs text-gray-400 mt-1">
                {canCreate ? 'Click "Add" to create the first requirement for this project.' : 'No requirements assigned to this project.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created Date &amp; Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Updated By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Updated Date &amp; Time</th>
                    {canEdit && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {requirements.map(req => (
                    <tr key={req.id} className={`hover:bg-gray-50 transition-colors ${!req.is_active ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 text-xs">{req.title}</div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 max-w-[200px]" title={req.content}>{req.content}</p>
                        {req.requirement_type === 'project' && req.project && (
                          <span className="text-xs text-blue-600">{req.project.project_name}</span>
                        )}
                        {req.requirement_type === 'activity' && req.activity && (
                          <span className="text-xs text-emerald-600">{req.activity.activity_name}</span>
                        )}
                        {!req.is_active && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap"><TypeBadge type={req.requirement_type} /></td>
                      <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {req.creator ? `${req.creator.first_name} ${req.creator.last_name}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDate(req.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {req.updater ? `${req.updater.first_name} ${req.updater.last_name}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">{formatDate(req.updated_at)}</td>
                      {canEdit && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button onClick={() => { setEditingReq(req); setEditDrawerOpen(true) }}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-700 border border-blue-200" title="Edit">
                            <FiFileText size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Nested edit drawer */}
      <EditDrawer
        isOpen={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        requirement={editingReq}
        onSaved={() => { setEditDrawerOpen(false); fetchRequirements() }}
      />
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const RequirementsManagement: React.FC = () => {
  const [drawerOpen, setDrawerOpen]       = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const { user } = useAuth()

  const handleOpenRequirements = (project: Project) => {
    setSelectedProject(project)
    setDrawerOpen(true)
  }

  return (
    <div>
      {/* Page header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FaClipboardList className="text-orange-600" size={22} />
          Project Requirements
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Select a project to view and manage its client requirements. Click the
          <span className="mx-1 inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded text-xs font-semibold">
            <FaClipboardList size={10} /> Requirements
          </span>
          button in the Actions column.
        </p>
      </div>

      {/* Projects table — show only projects assigned to current user (unless admin) */}
      <ProjectManagement 
        onRequirementsClick={handleOpenRequirements} 
        hideAddProjectButton={true}
        filterByCurrentUser={user?.role.level !== 1} // Only non-admin users see their assigned projects
      />

      {/* Client Requirements drawer */}
      <ProjectRequirementsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        project={selectedProject}
      />
    </div>
  )
}

export default RequirementsManagement
